import { useEffect, useMemo, useRef } from 'react';
import { useSorBySorId } from '@/hooks/useSorLookup';
import {
  nextAutoReceiverName,
  receiverNameFromThread,
} from '@/lib/medcom/forwardReceiverName';
import type { ParsedBundleContent } from '@/lib/medcom/parseCareCommunicationBundle';
import { isSorId } from '@/lib/medcom/sorEntityName';

/**
 * Videresend: fill the receiver name from the receiver SOR id. The thread's
 * MedCom letters win; otherwise the SOR lookup (communicate-sor-lookup).
 */
export function useForwardReceiverName(input: {
  enabled: boolean;
  sorId: string;
  name: string;
  setName: (name: string) => void;
  messages: ReadonlyArray<{ id: string; direction: 'in' | 'out' }> | undefined;
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined> | undefined;
  opened: ParsedBundleContent | null | undefined;
}): void {
  const { enabled, sorId, name, setName, messages, letters, opened } = input;
  const trimmed = sorId.trim();
  const validSor = enabled && isSorId(trimmed);
  const fromThread = useMemo(
    () =>
      validSor
        ? receiverNameFromThread({
            sorId: trimmed,
            messages: messages ?? [],
            letters: letters ?? new Map(),
            opened,
          })
        : null,
    [validSor, trimmed, messages, letters, opened],
  );
  const lookup = useSorBySorId(validSor && !fromThread ? trimmed : null);
  const fromLookup = lookup.data?.name?.trim() || null;
  const suggestion = fromThread ?? fromLookup;
  const loading = validSor && !fromThread && lookup.isLoading;
  const lastAutoFilled = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const next = nextAutoReceiverName({
      current: name,
      lastAutoFilled: lastAutoFilled.current,
      suggestion,
      loading,
    });
    if (next === undefined) return;
    lastAutoFilled.current = next || null;
    setName(next);
  }, [enabled, name, suggestion, loading, setName]);
}
